'use client';

import type { LeagueEvent } from '@interfaces/APITravel';
import { getInternationalFullDate } from '@utils/dates';
import {
  ArrowLeft,
  ArrowRight,
  Button,
  Calendar,
  CalendarIcon,
  Day,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@v2/ui';
import { format, isSameMonth } from 'date-fns';
import { useState, type Dispatch, type SetStateAction } from 'react';
import type { DateRange } from 'react-day-picker';
import { useMediaQuery } from 'usehooks-ts';
import { InputWithLabel } from './InputWithLabel';

type StayDateRange = {
  from: Date;
  to: Date;
};

export function StayDateRangeField({
  startDate,
  endDate,
  event,
  onChange,
  triggerClassName,
}: {
  startDate?: Date;
  endDate?: Date;
  event?: LeagueEvent;
  onChange: (range: StayDateRange) => void;
  triggerClassName?: string;
}) {
  const matches = useMediaQuery('(min-width: 768px)');
  const numberOfMonths = matches ? 2 : 1;
  const [isCalendarOpen, setCalendarOpen] = useState(false);
  const [date, setDate] = useState<DateRange | undefined>({
    from: startDate,
    to: endDate,
  });
  const [currentMonth, setCurrentMonth] = useState(
    () =>
      startDate ?? (event?.occurs_at ? new Date(event.occurs_at) : new Date())
  );

  const checkInOutValue = startDate
    ? endDate
      ? `${format(startDate, 'LLL dd, y')} - ${format(endDate, 'LLL dd, y')}`
      : format(startDate, 'LLL dd, y')
    : 'Pick a date';

  const eventDate = event?.occurs_at_local
    ? getInternationalFullDate(event.occurs_at_local, undefined)
    : null;

  const handleConfirm = () => {
    if (!date?.from || !date.to) return;
    onChange({ from: date.from, to: date.to });
    setCalendarOpen(false);
  };

  return (
    <Popover
      modal
      open={isCalendarOpen}
      onOpenChange={(open) => {
        if (open) {
          setDate({ from: startDate, to: endDate });
          if (startDate) setCurrentMonth(startDate);
        }
        setCalendarOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <InputWithLabel
          triggerClassName={triggerClassName}
          aria-label={
            startDate
              ? `Check in/out, ${checkInOutValue}`
              : 'Choose check-in and check-out dates'
          }
          icon={
            <CalendarIcon
              className="text-gray-800"
              pathProps={{ stroke: 'currentColor' }}
            />
          }
          label="Check in/out"
          value={checkInOutValue}
        />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={4}
        collisionPadding={16}
        className="no-scrollbar z-[80] max-h-[80dvh] w-auto max-w-[calc(100vw-16px)] overflow-auto p-0"
      >
        <Calendar
          numberOfMonths={numberOfMonths}
          disabled={{ before: new Date() }}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          weekStartsOn={1}
          initialFocus
          mode="range"
          selected={date}
          modifiers={
            eventDate
              ? {
                  // @ts-ignore Calendar event marker matches the shared hotel calendar.
                  event: eventDate,
                }
              : undefined
          }
          onSelect={setDate}
          components={{
            IconLeft: () => <ArrowLeft size="20" className="stroke-gray-800" />,
            IconRight: () => (
              <ArrowRight size="20" className="stroke-gray-800" />
            ),
            Day: (dayProps) => <Day {...dayProps} />,
            Footer: ({ displayMonth }) => (
              <CalendarFooter
                displayMonth={displayMonth}
                date={date}
                setDate={setDate}
                onConfirm={handleConfirm}
                onClose={() => setCalendarOpen(false)}
                initialDate={{ from: startDate, to: endDate }}
                currentMonth={currentMonth}
                numberOfMonths={numberOfMonths}
              />
            ),
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

type CalendarFooterProps = {
  date: DateRange | undefined;
  setDate: Dispatch<SetStateAction<DateRange | undefined>>;
  onConfirm: () => void;
  onClose?: () => void;
  initialDate?: DateRange;
  displayMonth: Date | undefined;
  currentMonth: Date;
  numberOfMonths: number;
  calendarMode?: 'single' | 'range';
};

export function CalendarFooter({
  setDate,
  date,
  onConfirm,
  onClose,
  initialDate,
  displayMonth,
  currentMonth,
  numberOfMonths,
  calendarMode,
}: CalendarFooterProps) {
  if (!displayMonth) return null;
  const isFirstMonth = isSameMonth(displayMonth, currentMonth);

  const handleCancel = () => {
    if (initialDate) {
      setDate(initialDate);
    }
    onClose?.();
  };

  if (numberOfMonths === 1)
    return (
      <tfoot className="flex items-center justify-between py-2">
        <Button
          variant="tertiary"
          className="text-gray-800"
          onClick={() => setDate({ from: undefined, to: undefined })}
        >
          Reset
        </Button>
        <div className="flex w-full justify-end space-x-3">
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            disabled={
              calendarMode === 'single' ? !date : !date?.from && !date?.to
            }
            onClick={onConfirm}
          >
            Apply
          </Button>
        </div>
      </tfoot>
    );
  return (
    <tfoot className="flex items-center justify-between py-2">
      {isFirstMonth && (
        <Button
          variant="tertiary"
          className="text-gray-800"
          onClick={() => setDate({ from: undefined, to: undefined })}
        >
          Reset
        </Button>
      )}
      {!isFirstMonth && (
        <div className="flex w-full justify-end space-x-3">
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            disabled={
              calendarMode === 'single' ? !date : !date?.from && !date?.to
            }
            onClick={onConfirm}
          >
            Apply
          </Button>
        </div>
      )}
    </tfoot>
  );
}
